import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import "./WalletReload.css";
import Layout from "../components/Layout/Layout";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const API = axios.create({
  baseURL: process.env.REACT_APP_API_BASE || "",
  withCredentials: true,
});

const toMoney2 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return (Math.round(n * 100) / 100).toFixed(2);
};

const TOKEN_KEYS = ["token", "authToken", "accessToken", "jwt"];

function readToken() {
  try {
    for (const k of TOKEN_KEYS) {
      const t = localStorage.getItem(k);
      if (t && String(t).trim()) return String(t).trim();
    }
  } catch (e) {}
  return "";
}

function getAuthHeaders() {
  const token = readToken();
  if (!token) return {};
  return {
    Authorization: `Bearer ${token}`,
    "x-auth-token": token,
  };
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString();
  } catch {
    return "-";
  }
}

function fmtReason(value) {
  const reason = String(value || "").trim();
  if (!reason) return "-";
  return reason
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function WalletReload() {
  const navigate = useNavigate();
  const query = useQuery();
  const { user } = useSelector((state) => state.user);

  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const [orderId, setOrderId] = useState("");
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");

  const pollTimerRef = useRef(null);
  const pollingRef = useRef(false);

  const reloadOrderIdFromUrl = String(query.get("reload_order_id") || "").trim();

  const presets = [50, 100, 200, 300, 500, 1000, 2000];
  const userName = String(user?.fname || user?.name || "").trim() || "User";
  const userEmail = String(user?.email || "").trim();
  const userInitial = userName.charAt(0).toUpperCase() || "U";
  const userAvatar = String(user?.picture || user?.avatar || "").trim();
  const walletBalance = Number(user?.balance) || 0;
  const balanceText = walletBalance.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const [whLoading, setWhLoading] = useState(false);
  const [whError, setWhError] = useState("");
  const [whItems, setWhItems] = useState([]);
  const [whPage, setWhPage] = useState(1);
  const [whLimit] = useState(10);
  const [whPages, setWhPages] = useState(1);
  const [whTotal, setWhTotal] = useState(0);

  const stopPolling = () => {
    pollingRef.current = false;
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const isFinal = (s) => ["SUCCESS", "FAILED", "CANCELLED"].includes(String(s || "").toUpperCase());

  const fetchWalletHistory = async (page = whPage) => {
    setWhLoading(true);
    setWhError("");
    try {
      const r = await API.get("/api/wallet/history", {
        params: { page, limit: whLimit },
        headers: getAuthHeaders(),
      });

      const data = r?.data || {};
      if (!data?.success) {
        setWhError(data?.message || "Failed to load wallet history");
        setWhItems([]);
        setWhLoading(false);
        return;
      }

      setWhItems(Array.isArray(data.items) ? data.items : []);
      setWhPage(Number(data.page) || page);
      setWhPages(Number(data.pages) || 1);
      setWhTotal(Number(data.total) || 0);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to load wallet history";
      setWhError(msg);
      setWhItems([]);
    } finally {
      setWhLoading(false);
    }
  };

  const pollNow = async (oid) => {
    const id = String(oid || "").trim();
    if (!id) return;

    try {
      const r = await API.post(
        "/api/wallet/reload/poll-now",
        { orderId: id },
        { headers: getAuthHeaders() }
      );

      const s = String(r?.data?.status || "").toUpperCase() || "PENDING";
      setStatus(s);

      if (s === "SUCCESS") {
        setMessage("Wallet reloaded successfully.");
        fetchWalletHistory(1);
      } else if (s === "FAILED") {
        setMessage("Payment failed. If money deducted, contact support with Order ID.");
      } else if (s === "CANCELLED") {
        setMessage("Payment cancelled.");
      } else {
        setMessage("Waiting for payment confirmation...");
      }

      if (isFinal(s)) stopPolling();
    } catch (e) {
      setMessage("Checking payment status...");
    }
  };

  const startPolling = (oid) => {
    const id = String(oid || "").trim();
    if (!id) return;

    stopPolling();
    pollingRef.current = true;

    pollNow(id);

    pollTimerRef.current = setInterval(() => {
      if (!pollingRef.current) return;
      pollNow(id);
    }, 2500);
  };

  useEffect(() => {
    if (reloadOrderIdFromUrl) {
      setOrderId(reloadOrderIdFromUrl);
      setStatus("PENDING");
      setMessage("Checking payment status...");
      startPolling(reloadOrderIdFromUrl);
    }

    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadOrderIdFromUrl]);

  useEffect(() => {
    fetchWalletHistory(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateAmount = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return { ok: false, msg: "Enter a valid amount" };
    if (n < 10) return { ok: false, msg: "Minimum reload is \u20b910" };
    if (n > 100000) return { ok: false, msg: "Maximum reload is \u20b9100000" };
    return { ok: true, n };
  };

  const onCreateReload = async () => {
    setMessage("");
    setStatus("");

    const check = validateAmount(amount);
    if (!check.ok) {
      setMessage(check.msg);
      return;
    }

    setLoading(true);
    try {
      const r = await API.post(
        "/api/wallet/reload/create",
        { amount: check.n },
        { headers: getAuthHeaders() }
      );

      const data = r?.data || {};
      if (!data?.success || !data?.payment_url || !data?.orderId) {
        setMessage(data?.message || "Failed to create payment");
        setLoading(false);
        return;
      }

      setOrderId(String(data.orderId));
      setStatus("PENDING");
      setMessage("Redirecting to payment...");

      window.location.href = String(data.payment_url);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Server error while creating reload";
      setMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const onReset = () => {
    stopPolling();
    setAmount("");
    setOrderId("");
    setStatus("");
    setMessage("");
    navigate("/add-balance", { replace: true });
  };

  const onPrevHistory = () => {
    if (whPage <= 1) return;
    fetchWalletHistory(whPage - 1);
  };

  const onNextHistory = () => {
    if (whPage >= whPages) return;
    fetchWalletHistory(whPage + 1);
  };

  const closeWallet = () => {
    navigate("/user-dashboard");
  };

  return (
    <Layout>
      <div className="wrPage">
        <button className="wrCloseBtn" type="button" onClick={closeWallet} aria-label="Close wallet reload">
          <CloseIcon />
        </button>

        <div className="wrModalStack">
          <section className="wrReloadCard" aria-label="Wallet reload">
            <div className="wrUserSummary">
              <span className="wrAvatar">
                {userAvatar ? <img src={userAvatar} alt={userName} /> : userInitial}
              </span>

              <div className="wrUserCopy">
                <h1>Hello, {userName}</h1>
                {userEmail && <p>{userEmail}</p>}
                <span>
                  Available Balance: <b>{"\u20b9"}{balanceText}</b>
                </span>
              </div>
            </div>

            {orderId ? (
              <div className="wrStatusBox">
                <div className="wrRow">
                  <span className="wrLabel">Reload Order ID</span>
                  <span className="wrValue mono">{orderId}</span>
                </div>

                <div className="wrRow">
                  <span className="wrLabel">Status</span>
                  <span className={`wrPill ${String(status).toLowerCase()}`}>
                    {status || "PENDING"}
                  </span>
                </div>

                <div className="wrMessage">{message}</div>

                <div className="wrActions">
                  {!isFinal(status) && (
                    <button className="wrBtn wrBtnGhost" type="button" onClick={() => pollNow(orderId)}>
                      Refresh
                    </button>
                  )}

                  <button className="wrBtn wrBtnPrimary" type="button" onClick={onReset}>
                    New Reload
                  </button>
                </div>

                <div className="wrHint">
                  If payment is successful but status stays pending, wait 10-20 seconds and press Refresh.
                </div>
              </div>
            ) : (
              <>
                <h2 className="wrModalTitle">Add Money</h2>

                <div className="wrInputWrap">
                  <span className="wrCurrency">{"\u20b9"}</span>
                  <input
                    className="wrInput"
                    type="number"
                    inputMode="decimal"
                    placeholder="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    disabled={loading}
                    aria-label="Reload amount"
                  />
                </div>

                <div className="wrInfoLine">
                  <span>Minimum amount is {"\u20b9"}10</span>
                  {amount && (
                    <span className="wrInfoRight">
                      You pay <b>{"\u20b9"}{toMoney2(amount)}</b>
                    </span>
                  )}
                </div>

                <div className="wrPresets" aria-label="Amount presets">
                  {presets.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`wrPreset ${String(amount) === String(p) ? "active" : ""}`}
                      disabled={loading}
                      onClick={() => setAmount(String(p))}
                    >
                      {"\u20b9"}{p}
                    </button>
                  ))}
                </div>

                {message ? <div className="wrAlert">{message}</div> : null}

                <button
                  className="wrBtn wrBtnPrimary wrBtnFull"
                  type="button"
                  onClick={onCreateReload}
                  disabled={loading}
                >
                  {loading ? "Creating..." : "Confirm"}
                </button>

                <div className="wrSecure">
                  <LockIcon />
                  <span>100% Secure &amp; Encrypted.</span>
                </div>
              </>
            )}
          </section>

          <section className="wrHistoryCard" aria-label="Wallet history">
            <div className="wrHistoryHead">
              <div>
                <h2>Wallet History</h2>
                <p>Total: {whTotal} | Page {whPage}/{whPages}</p>
              </div>

              <div className="wrBtnRow">
                <button
                  className="wrBtn wrBtnGhost"
                  type="button"
                  onClick={() => fetchWalletHistory(whPage)}
                  disabled={whLoading}
                >
                  {whLoading ? "Loading..." : "Refresh"}
                </button>
                <button
                  className="wrBtn wrBtnGhost"
                  type="button"
                  onClick={onPrevHistory}
                  disabled={whLoading || whPage <= 1}
                >
                  Prev
                </button>
                <button
                  className="wrBtn wrBtnGhost"
                  type="button"
                  onClick={onNextHistory}
                  disabled={whLoading || whPage >= whPages}
                >
                  Next
                </button>
              </div>
            </div>

            {whError ? <div className="wrAlert">{whError}</div> : null}

            <div className="wrMobileHistoryList">
              {whItems?.length ? (
                whItems.map((it) => {
                  const src = String(it?.source || "").toUpperCase();
                  const type = String(it?.type || "").toUpperCase();
                  const amt = toMoney2(it?.amount);
                  const st = String(it?.status || "").toUpperCase();
                  const statusText = st || "DONE";
                  const statusClass = st ? st.toLowerCase() : "success";
                  const balanceAfter = toMoney2(it?.balance_after);

                  return (
                    <article className="wrMobileHistoryItem" key={String(it?._id || it?.createdAt)}>
                      <div className="wrMobileHistoryTop">
                        <div>
                          <span className="wrMobileHistoryLabel">{src || "WALLET"}</span>
                          <strong>{"\u20b9"}{amt || "-"}</strong>
                        </div>

                        <span className={`wrPill ${statusClass}`}>
                          {statusText}
                        </span>
                      </div>

                      <div className="wrMobileHistoryMeta">
                        <span>{fmtDate(it?.createdAt)}</span>
                        <span className={`wrPill ${type === "DEBIT" ? "failed" : "success"}`}>
                          {type || "-"}
                        </span>
                      </div>

                      <div className="wrMobileHistoryFoot">
                        <span>{fmtReason(it?.reason)}</span>
                        <small>
                          Balance: {balanceAfter ? (
                            <>
                              {"\u20b9"}{balanceAfter}
                            </>
                          ) : "-"}
                        </small>
                      </div>
                    </article>
                  );
                })
              ) : (
                <div className="wrMobileHistoryEmpty">
                  {whLoading ? "Loading..." : "No history found"}
                </div>
              )}
            </div>

            <div className="wrTableWrap">
              <table className="wrTable">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Source</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Reason</th>
                    <th>Balance</th>
                  </tr>
                </thead>

                <tbody>
                  {whItems?.length ? (
                    whItems.map((it) => {
                      const src = String(it?.source || "").toUpperCase();
                      const type = String(it?.type || "").toUpperCase();
                      const amt = toMoney2(it?.amount);
                      const st = String(it?.status || "").toUpperCase();
                      const statusText = st || "DONE";
                      const statusClass = st ? st.toLowerCase() : "success";
                      const balanceAfter = toMoney2(it?.balance_after);

                      return (
                        <tr key={String(it?._id || it?.createdAt)}>
                          <td className="mono">{fmtDate(it?.createdAt)}</td>
                          <td>
                            <span className={`wrPill ${src === "RELOAD" ? "success" : "pending"}`}>
                              {src || "-"}
                            </span>
                          </td>
                          <td>
                            <span className={`wrPill ${type === "DEBIT" ? "failed" : "success"}`}>
                              {type || "-"}
                            </span>
                          </td>
                          <td className="mono">{"\u20b9"}{amt || "-"}</td>
                          <td>
                            <span className={`wrPill ${statusClass}`}>{statusText}</span>
                          </td>
                          <td>{fmtReason(it?.reason)}</td>
                          <td className="mono">
                            {balanceAfter ? (
                              <>
                                {"\u20b9"}{balanceAfter}
                              </>
                            ) : "-"}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="wrEmptyCell">
                        {whLoading ? "Loading..." : "No history found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}
